import { useEffect } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";

import { fetchCurrentUser } from "@/lib/auth";
import { useAuth, isAuthenticated, type UserRole } from "@/stores/auth";

interface ProtectedRouteProps {
  requireRole?: UserRole | UserRole[];
  /** Se true, redireciona pra /setup se !onboarding_completed. */
  requireOnboarded?: boolean;
}

export function ProtectedRoute({ requireRole, requireOnboarded = true }: ProtectedRouteProps) {
  const location = useLocation();
  const { user, hydrated } = useAuth();

  useEffect(() => {
    if (hydrated && isAuthenticated()) {
      void fetchCurrentUser();
    }
  }, [hydrated]);

  if (!hydrated) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        Carregando...
      </div>
    );
  }

  if (!isAuthenticated()) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Super admin / admin nunca fazem onboarding (são staff Eunerd com acesso total).
  const isStaff = user && (user.role === "super_admin" || user.role === "admin");

  // user antigo (pré-deploy) pode ter onboarding_completed undefined.
  // Só redireciona pra /setup quando explicitamente false (user novo precisa setup),
  // E não é staff Eunerd.
  if (
    requireOnboarded &&
    user &&
    !isStaff &&
    user.onboarding_completed === false &&
    location.pathname !== "/setup"
  ) {
    return <Navigate to="/setup" replace />;
  }

  if (requireRole && user) {
    const roles = Array.isArray(requireRole) ? requireRole : [requireRole];
    if (!roles.includes(user.role)) {
      return (
        <div className="min-h-screen flex items-center justify-center px-4">
          <div className="max-w-md text-center space-y-3">
            <h1 className="text-2xl font-bold">Acesso negado</h1>
            <p className="text-muted-foreground">
              Esta área requer permissão de{" "}
              <span className="font-mono text-foreground">{roles.join(" ou ")}</span>.
              Sua role atual é{" "}
              <span className="font-mono text-foreground">{user.role}</span>.
            </p>
          </div>
        </div>
      );
    }
  }

  return <Outlet />;
}
